/**
 * Motor de precificação.
 *
 * Porte fiel das regras de `construtorcs.html` e `construtorps.html`, especificadas em
 * `docs/regras-negocio.md`. As divergências deliberadas em relação ao legado estão marcadas com
 * `CORREÇÃO` e justificadas no ponto de uso.
 *
 * Moeda funcional interna: USD. A trilha PS recebe os valores em BRL e os converte na entrada,
 * exatamente como o legado faz, e a conversão de volta acontece só na apresentação.
 */

import {
  CONFIGURACAO_PADRAO,
  type Configuracao,
  type FaixaSafeStorage,
  type RegraSuporte,
} from './configuracao.ts';
import { arredondar, brlParaUsd, usdParaBrl, valorDeUsd } from './dinheiro.ts';
import {
  ErroPrecificacao,
  type EntradaPrecificacao,
  type EntradaSafeStorage,
  type ItemPrecificado,
  type LicencaBackup,
  type Moeda,
  type PlanoSafeStorage,
  type ResultadoPrecificacao,
  type Trilha,
} from './tipos.ts';

const LICENCAS: readonly LicencaBackup[] = [
  'fileServer',
  'virtualServer',
  'sharepoint',
  'vmwareHyperv',
];

// ---------------------------------------------------------------------------
// SafeStorage
// ---------------------------------------------------------------------------

/**
 * Faixa configurada de um plano do SafeStorage.
 *
 * Lança em plano inexistente em vez de devolver `undefined`: um plano fora da tabela é erro de
 * programação, e devolver nulo espalharia a verificação por todos os pontos de uso.
 */
export function faixaSafeStorage(
  plano: PlanoSafeStorage,
  config: Configuracao = CONFIGURACAO_PADRAO,
): FaixaSafeStorage {
  const faixa = config.safestorage.faixas.find((f) => f.plano === plano);
  if (faixa === undefined) {
    throw new ErroPrecificacao(
      'SAFESTORAGE_FORA_DA_FAIXA',
      `O plano SafeStorage "${plano}" não existe na tabela da configuração ${config.versao}.`,
      { plano, versaoConfiguracao: config.versao },
    );
  }
  return faixa;
}

/**
 * Faixa adequada a uma quantidade de usuários.
 *
 * Existe para a interface sugerir o plano a partir da contagem que o vendedor digita, em vez de
 * obrigá-lo a consultar a tabela de preços — errar a faixa é errar o preço da proposta.
 *
 * A última faixa tem `usuariosAte` nulo e funciona como o "acima de", então qualquer contagem sempre
 * encontra uma faixa.
 */
export function faixaSafeStoragePorUsuarios(
  usuarios: number,
  config: Configuracao = CONFIGURACAO_PADRAO,
): FaixaSafeStorage {
  const faixas = config.safestorage.faixas;
  const encontrada = faixas.find((f) => f.usuariosAte !== null && usuarios <= f.usuariosAte);
  if (encontrada !== undefined) return encontrada;

  const ultima = faixas[faixas.length - 1];
  if (ultima === undefined) {
    throw new ErroPrecificacao(
      'SAFESTORAGE_FORA_DA_FAIXA',
      `A configuração ${config.versao} não declara nenhuma faixa do SafeStorage.`,
      { versaoConfiguracao: config.versao },
    );
  }
  return ultima;
}

/**
 * Mensalidade da assinatura SafeStorage, em USD.
 *
 * Preço de tabela, sem margem: a faixa já é preço de venda. A margem do produto está no
 * armazenamento, que percorre o caminho normal do consumo.
 */
export function mensalidadeSafeStorageUsd(
  entrada: EntradaSafeStorage,
  config: Configuracao = CONFIGURACAO_PADRAO,
): number {
  const faixa = faixaSafeStorage(entrada.plano, config);

  // Faixa sob consulta: quem define o preço é a negociação, não a tabela.
  if (faixa.mensalidadeUsd === null) {
    if (entrada.mensalidadeCustomizadaUsd === null) {
      throw new ErroPrecificacao(
        'SAFESTORAGE_MENSALIDADE_AUSENTE',
        `O plano ${faixa.nomeComercial} é sob consulta: informe a mensalidade negociada em dólar.`,
        { plano: entrada.plano },
      );
    }
    return entrada.mensalidadeCustomizadaUsd;
  }

  return faixa.mensalidadeUsd;
}

// ---------------------------------------------------------------------------
// Margem
// ---------------------------------------------------------------------------

/**
 * Margem aplicável, em pontos percentuais.
 *
 * Em CS depende do plano de suporte e da duração do contrato — o que significa que **trocar o plano
 * altera o preço da infraestrutura**, não apenas o custo do suporte. Efeito colateral herdado do
 * legado, preservado no cálculo e explicitado na interface (defeito D3).
 *
 * Em PS a margem é fixa, porque cobre o custo de participação em licitações.
 *
 * Exceção do SafeStorage: quando o produto está na proposta, a faixa de margem é a do plano
 * declarado em `config.safestorage.planoMargem` (Developer), e não a do plano de suporte escolhido.
 * O SafeStorage é produto de catálogo com preço próprio; deixar a margem do armazenamento seguir o
 * plano de suporte faria o mesmo produto sair a 25% ou a 30% conforme um campo que trata de outro
 * assunto. A troca é anunciada em `avisos`, para o vendedor não descobrir pela conta.
 */
export function margemAplicavel(
  entrada: Pick<EntradaPrecificacao, 'trilha' | 'planoSuporte' | 'duracaoContratoMeses'> & {
    readonly safestorage?: EntradaSafeStorage | null;
  },
  config: Configuracao = CONFIGURACAO_PADRAO,
): number {
  const trilha = config.trilhas[entrada.trilha];

  if (trilha.margemFixa !== null) return trilha.margemFixa;
  if (trilha.margens === null) return trilha.margemFallback;

  const plano =
    entrada.safestorage === undefined || entrada.safestorage === null
      ? entrada.planoSuporte
      : config.safestorage.planoMargem;

  // A tabela é declarada com `satisfies Record<PlanoSuporte, ...>`, então o compilador garante que
  // todo plano e toda duração existem. Acrescentar um plano novo sem margem quebra o build.
  return trilha.margens[plano][entrada.duracaoContratoMeses];
}

/**
 * Aplica a margem por gross-up: `preco = custo / (1 - margem/100)`.
 *
 * É margem de contribuição sobre o preço de venda, não markup sobre o custo. A aplicação é
 * item a item, nunca sobre o total, porque os resultados divergem por arredondamento.
 */
export function aplicarMargem(custoLiquido: number, margemPercentual: number): number {
  const divisor = 1 - margemPercentual / 100;
  if (divisor <= 0) {
    throw new ErroPrecificacao(
      'CONSUMO_NEGATIVO',
      `Margem de ${margemPercentual}% é inválida: o divisor do gross-up seria zero ou negativo.`,
      { margemPercentual },
    );
  }
  return custoLiquido / divisor;
}

// ---------------------------------------------------------------------------
// Suporte
// ---------------------------------------------------------------------------

function converterParaUsd(valor: number, moeda: Moeda, ptax: number | null): number {
  if (moeda === 'USD') return valor;
  if (ptax === null) {
    throw new ErroPrecificacao(
      'PTAX_OBRIGATORIO',
      'Conversão de BRL para USD exige a cotação do dólar (PTAX).',
    );
  }
  return brlParaUsd(valor, ptax);
}

function converterDeUsd(valorUsd: number, moeda: Moeda, ptax: number | null): number {
  if (moeda === 'USD') return valorUsd;
  if (ptax === null) {
    throw new ErroPrecificacao(
      'PTAX_OBRIGATORIO',
      'Conversão de USD para BRL exige a cotação do dólar (PTAX).',
    );
  }
  return usdParaBrl(valorUsd, ptax);
}

/**
 * Custo mensal do plano de suporte, em USD.
 *
 * A base de cálculo é o **consumo já com margem** (preço de venda), não o custo líquido da AWS.
 *
 * Duas formas de piso, ambas do legado:
 * - sem gatilho (CS): `max(piso, base × percentual)`
 * - com gatilho (PS): `base >= gatilho ? base × percentual : piso`
 *
 * As duas coincidem no ponto de virada, mas divergem abaixo dele quando o gatilho não é igual a
 * `piso / percentual`. Reproduzidas separadamente de propósito.
 */
export function custoSuporteUsd(
  consumoUsd: number,
  regra: RegraSuporte,
  ptax: number | null,
): number {
  if (regra.tipo === 'gratuito') return 0;
  if (regra.tipo === 'fixo') return converterParaUsd(regra.valor, regra.moeda, ptax);

  const baseNaMoedaDaRegra = converterDeUsd(consumoUsd, regra.moeda, ptax);
  const proporcional = baseNaMoedaDaRegra * regra.percentual;

  const custoNaMoedaDaRegra =
    regra.gatilho === null
      ? Math.max(regra.pisoMensal, proporcional)
      : baseNaMoedaDaRegra >= regra.gatilho
        ? proporcional
        : regra.pisoMensal;

  return converterParaUsd(custoNaMoedaDaRegra, regra.moeda, ptax);
}

// ---------------------------------------------------------------------------
// Validação
// ---------------------------------------------------------------------------

/** Soma das licenças de backup contratadas. Zero quando não há backup na proposta. */
export function totalLicencasBackup(entrada: Pick<EntradaPrecificacao, 'backup'>): number {
  return LICENCAS.reduce((soma, l) => soma + (entrada.backup?.[l] ?? 0), 0);
}

/**
 * Soma dos custos adicionais, convertida para USD.
 *
 * Soma primeiro na moeda de entrada e converte uma única vez, como o backup faz. Converter item a item
 * introduziria um arredondamento por linha que não existe em nenhum outro produto do catálogo, e a
 * soma das conversões divergiria da conversão da soma.
 *
 * Sem margem: o valor informado pelo vendedor já é preço de venda.
 *
 * Em CS a moeda de entrada é USD e a conversão é identidade, então esta função **não** cria dependência
 * de PTAX onde ela não existia. É o que diferencia o campo do backup, que por ser sempre cotado em BRL
 * passou a exigir PTAX inclusive em CS (defeito D2).
 */
export function totalCustosAdicionaisUsd(
  entrada: Pick<EntradaPrecificacao, 'custosAdicionais'>,
  moedaEntrada: Moeda,
  ptax: number | null,
): number {
  const itens = entrada.custosAdicionais ?? [];
  if (itens.length === 0) return 0;

  let total = 0;
  for (const item of itens) total += item.valorMensal;

  return converterParaUsd(total, moedaEntrada, ptax);
}

/**
 * `true` quando o cálculo depende da cotação do dólar.
 *
 * CORREÇÃO (defeito D2): o backup é precificado em BRL e passa a compor a mensalidade nas duas
 * trilhas. Por isso o PTAX deixa de ser exclusivo do PS e passa a ser exigido sempre que houver
 * licenças, inclusive em CS. O legado evitava o problema omitindo o backup do total do CS.
 *
 * É **exportada** porque a interface precisa da mesma resposta que o motor: é ela que decide se
 * mostra o campo de cotação ao vendedor. Enquanto essa regra viveu só aqui, o painel escondia o
 * campo em CS e o vendedor que marcasse backup travava na publicação, sem ter onde informar o PTAX
 * que o motor exigia. Duplicar a condição no painel reabriria o mesmo buraco na próxima mudança.
 */
export function exigePtax(entrada: EntradaPrecificacao): boolean {
  return entrada.trilha === 'PS' || totalLicencasBackup(entrada) > 0;
}

function validar(entrada: EntradaPrecificacao, config: Configuracao, totalLicencas: number): void {
  const trilha = config.trilhas[entrada.trilha];

  if (!trilha.planosDisponiveis.includes(entrada.planoSuporte)) {
    throw new ErroPrecificacao(
      'PLANO_INDISPONIVEL_NA_TRILHA',
      `O plano "${entrada.planoSuporte}" não está disponível na trilha ${entrada.trilha}. ` +
        `Disponíveis: ${trilha.planosDisponiveis.join(', ')}.`,
      { trilha: entrada.trilha, plano: entrada.planoSuporte },
    );
  }

  if (exigePtax(entrada) && entrada.ptax === null) {
    throw new ErroPrecificacao(
      'PTAX_OBRIGATORIO',
      entrada.trilha === 'PS'
        ? 'A trilha PS exige a cotação do dólar (PTAX).'
        : 'As licenças de backup são cotadas em BRL e exigem a cotação do dólar (PTAX).',
      { trilha: entrada.trilha, totalLicencas },
    );
  }

  if (entrada.ptax !== null && (!Number.isFinite(entrada.ptax) || entrada.ptax <= 0)) {
    throw new ErroPrecificacao(
      'PTAX_INVALIDO',
      `Cotação do dólar inválida: ${entrada.ptax}. Deve ser um número maior que zero.`,
      { ptax: entrada.ptax },
    );
  }

  const semCsv = entrada.itensCusto === null || entrada.itensCusto.length === 0;
  if (semCsv && entrada.consumoManual === null) {
    throw new ErroPrecificacao(
      'CONSUMO_AUSENTE',
      'Sem itens do CSV da calculadora, é obrigatório informar o consumo manualmente.',
    );
  }

  if (entrada.consumoManual !== null && entrada.consumoManual < 0) {
    throw new ErroPrecificacao('CONSUMO_NEGATIVO', 'O consumo não pode ser negativo.', {
      consumoManual: entrada.consumoManual,
    });
  }

  for (const item of entrada.itensCusto ?? []) {
    if (item.custoMensalLiquidoUsd < 0) {
      throw new ErroPrecificacao(
        'CONSUMO_NEGATIVO',
        `O item "${item.servico}" tem custo negativo.`,
        { servico: item.servico, custo: item.custoMensalLiquidoUsd },
      );
    }
  }

  if (entrada.credito !== null && entrada.credito.valorUsd < 0) {
    throw new ErroPrecificacao('CREDITO_NEGATIVO', 'O crédito AWS não pode ser negativo.', {
      valorUsd: entrada.credito.valorUsd,
    });
  }

  if (entrada.deploy.modalidade !== 'nao_incluido' && entrada.deploy.custo < 0) {
    throw new ErroPrecificacao('DEPLOY_NEGATIVO', 'O custo de deploy não pode ser negativo.', {
      custo: entrada.deploy.custo,
    });
  }

  for (const licenca of LICENCAS) {
    const quantidade = entrada.backup?.[licenca] ?? 0;
    if (quantidade < 0 || !Number.isInteger(quantidade)) {
      throw new ErroPrecificacao(
        'LICENCA_NEGATIVA',
        `A quantidade de licenças "${licenca}" deve ser um inteiro não negativo.`,
        { licenca, quantidade },
      );
    }
  }

  if (entrada.safestorage !== null) {
    const { plano, usuarios, mensalidadeCustomizadaUsd } = entrada.safestorage;

    if (!Number.isInteger(usuarios) || usuarios < 1) {
      throw new ErroPrecificacao(
        'SAFESTORAGE_USUARIOS_INVALIDOS',
        'A quantidade de usuários do SafeStorage deve ser um inteiro maior que zero.',
        { usuarios },
      );
    }

    const faixa = faixaSafeStorage(plano, config);

    /*
     * Só o teto é bloqueado, e a razão é a assimetria do erro. Escolher uma faixa menor que a
     * contagem de usuários subfatura o contrato — o cliente assina 40 usuários pagando a faixa de 15,
     * e a diferença aparece depois como reajuste que ninguém combinou. Escolher uma faixa maior é
     * apenas conservador, e há caso legítimo: contratar Singularidade prevendo a contratação do ano.
     */
    if (faixa.usuariosAte !== null && usuarios > faixa.usuariosAte) {
      throw new ErroPrecificacao(
        'SAFESTORAGE_FORA_DA_FAIXA',
        `${faixa.nomeComercial} atende até ${faixa.usuariosAte} usuários, e a proposta declara ${usuarios}. ` +
          `Selecione ${faixaSafeStoragePorUsuarios(usuarios, config).nomeComercial}.`,
        { plano, usuarios, usuariosAte: faixa.usuariosAte },
      );
    }

    if (mensalidadeCustomizadaUsd !== null && mensalidadeCustomizadaUsd < 0) {
      throw new ErroPrecificacao(
        'SAFESTORAGE_MENSALIDADE_AUSENTE',
        'A mensalidade negociada do SafeStorage não pode ser negativa.',
        { mensalidadeCustomizadaUsd },
      );
    }

    if (faixa.mensalidadeUsd === null && mensalidadeCustomizadaUsd === null) {
      throw new ErroPrecificacao(
        'SAFESTORAGE_MENSALIDADE_AUSENTE',
        `O plano ${faixa.nomeComercial} é sob consulta: informe a mensalidade negociada em dólar.`,
        { plano },
      );
    }
  }

  /*
   * Custos adicionais.
   *
   * A descrição é validada aqui, e não só no schema do documento, porque a prévia do painel chama o
   * motor antes de montar o documento: sem esta verificação o vendedor veria um valor somado à
   * mensalidade por uma linha sem nome, e só descobriria o problema ao publicar.
   */
  for (const [indice, item] of (entrada.custosAdicionais ?? []).entries()) {
    if (item.descricao.trim() === '') {
      throw new ErroPrecificacao(
        'CUSTO_ADICIONAL_SEM_DESCRICAO',
        `O custo adicional na posição ${String(indice + 1)} está sem descrição. ` +
          'Informe o nome do item como ele deve aparecer na proposta.',
        { indice, valorMensal: item.valorMensal },
      );
    }

    if (!Number.isFinite(item.valorMensal) || item.valorMensal < 0) {
      throw new ErroPrecificacao(
        'CUSTO_ADICIONAL_NEGATIVO',
        `O custo adicional "${item.descricao}" tem valor inválido: ${String(item.valorMensal)}. ` +
          'Deve ser um número não negativo.',
        { descricao: item.descricao, valorMensal: item.valorMensal },
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Motor
// ---------------------------------------------------------------------------

/**
 * Calcula a composição completa do preço.
 *
 * Ordem, conforme `docs/regras-negocio.md`, seção 9:
 * consumo com margem, custo de suporte, base mensal, alocação do crédito, redução mensal,
 * mensalidade estimada, deploy faturável e percentual de economia.
 */
export function precificar(
  entrada: EntradaPrecificacao,
  config: Configuracao = CONFIGURACAO_PADRAO,
): ResultadoPrecificacao {
  const avisos: string[] = [];

  const totalLicencas = totalLicencasBackup(entrada);
  validar(entrada, config, totalLicencas);

  const trilha = config.trilhas[entrada.trilha];
  const ptax = entrada.ptax;
  const moedaEntrada = trilha.moedaEntrada;

  // --- 1. Consumo ---------------------------------------------------------
  // A margem só é aplicada quando há CSV. Sem CSV, o valor digitado já é preço de venda.
  const temCsv = entrada.itensCusto !== null && entrada.itensCusto.length > 0;
  const margemPercentual = temCsv ? margemAplicavel(entrada, config) : 0;

  /*
   * A troca de faixa de margem pelo SafeStorage é anunciada, não silenciosa.
   *
   * Só avisa quando muda algo de fato: em PS a margem é fixa, e em CS os planos `nenhum` e
   * `developer` já usam a faixa do Developer. Avisar nesses casos seria ruído sobre uma conta que não
   * mudou.
   */
  if (temCsv && entrada.safestorage !== null) {
    const margemDoPlanoDeSuporte = margemAplicavel({ ...entrada, safestorage: null }, config);
    if (margemDoPlanoDeSuporte !== margemPercentual) {
      avisos.push(
        `O eMaster SafeStorage é vendido com a margem da faixa Developer: ${margemPercentual}% ` +
          `em vez dos ${margemDoPlanoDeSuporte}% do plano de suporte selecionado.`,
      );
    }
  }

  let consumoUsd: number;
  let custoLiquidoTotalUsd: number | null = null;
  let itensPrecificados: readonly ItemPrecificado[] = [];

  if (temCsv) {
    const itens = entrada.itensCusto;

    let liquido = 0;
    let comMargem = 0;
    const precificados: ItemPrecificado[] = [];

    for (const item of itens) {
      const precoVendaUsd = aplicarMargem(item.custoMensalLiquidoUsd, margemPercentual);
      liquido += item.custoMensalLiquidoUsd;
      comMargem += precoVendaUsd;
      precificados.push({ ...item, precoVendaUsd: arredondar(precoVendaUsd) });
    }

    custoLiquidoTotalUsd = liquido;
    consumoUsd = comMargem;
    itensPrecificados = precificados;
  } else {
    const manual = entrada.consumoManual ?? 0;
    consumoUsd = converterParaUsd(manual, moedaEntrada, ptax);
    avisos.push(
      'Nenhum CSV da calculadora foi importado: nenhuma margem foi aplicada e o consumo ' +
        'informado é tratado como preço de venda já formado.',
    );
  }

  // --- 2. Suporte ---------------------------------------------------------
  const regra = trilha.regrasSuporte[entrada.planoSuporte];
  if (regra === undefined) {
    throw new ErroPrecificacao(
      'PLANO_INDISPONIVEL_NA_TRILHA',
      `Não há regra de cobrança configurada para o plano "${entrada.planoSuporte}" na trilha ${entrada.trilha}.`,
      { trilha: entrada.trilha, plano: entrada.planoSuporte },
    );
  }
  const custoSuporte = custoSuporteUsd(consumoUsd, regra, ptax);

  const baseMensalUsd = consumoUsd + custoSuporte;

  // --- 3. Deploy ----------------------------------------------------------
  const incluiDeploy = entrada.deploy.modalidade !== 'nao_incluido';
  const deployCustoUsd = incluiDeploy
    ? converterParaUsd(entrada.deploy.custo, moedaEntrada, ptax)
    : 0;

  const cortesia = entrada.deploy.modalidade === 'cortesia';
  const financiado = entrada.deploy.modalidade === 'financiado';

  if (cortesia && entrada.trilha === 'CS') {
    avisos.push(
      'Deploy como cortesia não existia na trilha CS do sistema anterior. O valor foi computado ' +
        'como desconto no percentual de economia, por consistência com a trilha PS.',
    );
  }

  // --- 4. Crédito ---------------------------------------------------------
  // O deploy tem prioridade no consumo do crédito; o residual é diluído na mensalidade.
  const creditoTotalUsd = entrada.credito?.valorUsd ?? 0;
  const janelaMeses = entrada.credito?.janelaMeses ?? config.credito.janelaPadrao;

  const creditoNoDeployUsd = financiado ? Math.min(creditoTotalUsd, deployCustoUsd) : 0;
  const creditoResidualUsd = creditoTotalUsd - creditoNoDeployUsd;
  const reducaoMensalUsd = creditoResidualUsd / janelaMeses;

  // --- 5. Backup ----------------------------------------------------------
  let backupBrl = 0;
  for (const licenca of LICENCAS) {
    backupBrl += (entrada.backup?.[licenca] ?? 0) * config.backup.precos[licenca];
  }
  // Preços de licença já são preços de venda: nenhuma margem é aplicada.
  const backupUsd = backupBrl === 0 ? 0 : converterParaUsd(backupBrl, config.backup.moeda, ptax);

  // --- 5b. SafeStorage ----------------------------------------------------
  /*
   * A assinatura da plataforma é preço de tabela em dólar, sem margem — e é só a plataforma. O
   * armazenamento S3 do SafeStorage entra por `consumo`, com margem, porque é consumo de
   * infraestrutura como qualquer outro recurso AWS.
   *
   * Ser cotada em dólar é o que mantém o SafeStorage fora de `exigePtax`: diferente das licenças de
   * backup, aqui não há conversão de moeda para fazer.
   */
  const safestorageUsd =
    entrada.safestorage === null ? 0 : mensalidadeSafeStorageUsd(entrada.safestorage, config);

  // --- 5c. Custos adicionais ----------------------------------------------
  /*
   * Itens nomeados pelo vendedor, na moeda de entrada da trilha: USD em CS, BRL em PS.
   *
   * Sem margem, como os demais produtos de catálogo — o valor digitado é o que o cliente paga. A
   * escolha de seguir a moeda da trilha, em vez de fixar uma, é o que mantém o campo fora de
   * `exigePtax`: em CS não há conversão a fazer, e em PS o PTAX já era obrigatório.
   */
  const custosAdicionaisUsd = totalCustosAdicionaisUsd(entrada, moedaEntrada, ptax);

  // --- 6. Mensalidade -----------------------------------------------------
  // CORREÇÃO (defeito D2): o backup compõe a mensalidade nas duas trilhas. O legado somava só no PS.
  //
  // A redução do crédito é aplicada sobre o conjunto, e é isso que faz o Crédito Especial AWS
  // incidir também sobre a assinatura da plataforma SafeStorage, e não apenas sobre a infraestrutura.
  const mensalidadeUsd =
    baseMensalUsd + backupUsd + safestorageUsd + custosAdicionaisUsd - reducaoMensalUsd;

  // CORREÇÃO (defeito D1): o legado publicava mensalidade negativa sem qualquer aviso.
  if (mensalidadeUsd < 0) {
    throw new ErroPrecificacao(
      'MENSALIDADE_NEGATIVA',
      `A mensalidade estimada ficaria negativa (${arredondar(mensalidadeUsd)} USD). ` +
        'Reduza o valor do crédito AWS ou alongue a janela de distribuição.',
      {
        mensalidadeUsd: arredondar(mensalidadeUsd),
        baseMensalUsd: arredondar(baseMensalUsd),
        backupUsd: arredondar(backupUsd),
        safestorageUsd: arredondar(safestorageUsd),
        custosAdicionaisUsd: arredondar(custosAdicionaisUsd),
        reducaoMensalUsd: arredondar(reducaoMensalUsd),
        janelaMeses,
      },
    );
  }

  // --- 7. Deploy faturável ------------------------------------------------
  // Cortesia zera o faturamento, mas o valor continua exibido para evidenciar o benefício.
  const deployFaturadoUsd = cortesia ? 0 : Math.max(0, deployCustoUsd - creditoNoDeployUsd);

  // --- 8. Economia --------------------------------------------------------
  /*
   * O denominador é a base mensal e o deploy — produtos de catálogo ficam fora.
   *
   * O backup já era tratado assim, e o SafeStorage segue a mesma regra por consistência: incluir só
   * um dos dois produziria dois percentuais de economia calculados com réguas diferentes na mesma
   * tabela de preços. Mexer nessa base mudaria o número de propostas já emitidas, então a mudança,
   * se vier, é decisão de negócio registrada — não efeito colateral de um produto novo.
   */
  const economiaPercentual = calcularEconomia({
    trilha: entrada.trilha,
    baseMensalUsd,
    janelaMeses,
    deployCustoUsd,
    incluiDeploy,
    financiado,
    cortesia,
    creditoTotalUsd,
  });

  return {
    versaoConfiguracao: config.versao,
    ptaxAplicado: ptax,

    margemPercentual,
    margemAplicada: temCsv,
    itensPrecificados,

    custoLiquidoTotal:
      custoLiquidoTotalUsd === null ? null : valorDeUsd(custoLiquidoTotalUsd, ptax),
    consumo: valorDeUsd(consumoUsd, ptax),
    custoSuporte: valorDeUsd(custoSuporte, ptax),
    baseMensal: valorDeUsd(baseMensalUsd, ptax),

    licencasBackup: valorDeUsd(backupUsd, ptax),
    plataformaSafeStorage: valorDeUsd(safestorageUsd, ptax),
    custosAdicionais: valorDeUsd(custosAdicionaisUsd, ptax),

    deployCustoTotal: valorDeUsd(deployCustoUsd, ptax),
    deployFaturado: valorDeUsd(deployFaturadoUsd, ptax),
    creditoAplicadoNoDeploy: valorDeUsd(creditoNoDeployUsd, ptax),
    creditoResidual: valorDeUsd(creditoResidualUsd, ptax),
    reducaoMensal: valorDeUsd(reducaoMensalUsd, ptax),

    mensalidadeEstimada: valorDeUsd(mensalidadeUsd, ptax),

    economiaPercentual: arredondar(economiaPercentual, 4),
    // Reproduz o legado: o bloco só é renderizado quando há crédito, ainda que a cortesia
    // isoladamente produza economia. Ver decisão pendente P3.
    exibirEconomia: creditoTotalUsd > 0,

    avisos,
  };
}

interface ParametrosEconomia {
  readonly trilha: Trilha;
  readonly baseMensalUsd: number;
  readonly janelaMeses: number;
  readonly deployCustoUsd: number;
  readonly incluiDeploy: boolean;
  readonly financiado: boolean;
  readonly cortesia: boolean;
  readonly creditoTotalUsd: number;
}

/**
 * Percentual de economia no período de distribuição do crédito.
 *
 * As duas trilhas divergem no legado e a divergência é preservada:
 * - CS exclui do denominador o deploy quando ele é financiado.
 * - PS sempre inclui o deploy no denominador e soma a cortesia ao numerador.
 */
export function calcularEconomia(p: ParametrosEconomia): number {
  if (p.trilha === 'CS') {
    const denominador = p.baseMensalUsd * p.janelaMeses + (p.financiado ? 0 : p.deployCustoUsd);
    const numerador = p.creditoTotalUsd + (p.cortesia ? p.deployCustoUsd : 0);
    return denominador > 0 ? (numerador / denominador) * 100 : 0;
  }

  const denominador = p.baseMensalUsd * p.janelaMeses + (p.incluiDeploy ? p.deployCustoUsd : 0);
  const numerador = p.creditoTotalUsd + (p.cortesia ? p.deployCustoUsd : 0);
  return denominador > 0 ? (numerador / denominador) * 100 : 0;
}
